import { createEffect, sample } from 'effector';
import { t } from 'i18n';
import _, { keyBy } from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import { $global, storage } from 'models/store-global';

import AlertsService from 'services/AlertsService';

import { eventDispatch } from 'hooks/useEventListener';

import { type MyStore } from 'types/Store';
import { type AdvisorsChanges } from 'types/zod/advisor-changes';
import { AdvisorsGroup } from 'types/zod/advisors-group';
import { type Client } from 'types/zod/client';

const saveAdvisorsChangesFx = createEffect((changes: AdvisorsChanges) => {
    if (changes.groups) {
        changes.groups.forEach((group) => {
            storage.set(
                (store): MyStore => ({
                    ...store,
                    advisor: {
                        ...(store.advisor || {}),
                        groups: {
                            ...(store.advisor?.groups || {}),
                            [group.groupId]: group,
                        },
                    },
                }),
            );
            if (group.children && group.children.groups) {
                group.children.groups.forEach((childGroup) => {
                    storage.set(
                        (store): MyStore => ({
                            ...store,
                            advisor: {
                                ...(store.advisor || {}),
                                groups: {
                                    ...(store.advisor?.groups || {}),
                                    [childGroup.groupId]: {
                                        ...(store.advisor?.groups?.[group.groupId] || {}),
                                        name: childGroup.name,
                                        parentGroupId: childGroup.parentGroupId,
                                    } as AdvisorsGroup,
                                },
                            },
                        }),
                    );
                });
            }
        });
    }
    if (changes.participants) {
        storage.set(
            (state): MyStore => ({
                ...state,
                participants: {
                    ...(state.participants || {}),
                    ...keyBy(changes.participants, 'participant_id'),
                },
            }),
        );
    }
});

// basic error handling
sample({
    clock: [
        $$api.advisor.getClientsFx.failData,
        $$api.advisor.removeClientFx.failData,
        $$api.advisor.getAdvisorInviteCodesFx.failData,
        $$api.advisor.getAdvisorByCodeFx.failData,
        $$api.advisor.linkAdvisorFx.failData,
        $$api.advisor.createZendeskTicketFx.failData,
        $$api.advisor.getAllGroupsFx.failData,
        $$api.advisor.getGroupFx.failData,
        $$api.advisor.createGroupFx.failData,
        $$api.advisor.removeAdvisorFromGroupFx.failData,
        $$api.advisor.moveAdvisorToGroupFx.failData,
        $$api.advisor.moveGroupToNewParentGroupFx.failData,
        $$api.advisor.editGroupFx.failData,
        $$api.advisor.unlinkClientFromAdvisorFx.failData,
        $$api.advisor.linkClientToAdvisorFx.failData,
        $$api.advisor.linkAdvisorToGroupFx.failData,
        $$api.advisor.sendInviteFx.failData,
        $$api.advisor.getGroupByCodeFx.failData,
        $$api.advisor.handoverClientFx.failData,
        $$api.advisor.searchFx.failData,
        $$api.advisor.setAdvisorPricingOptionsFx.failData,
        $$api.advisor.setGroupPricingOptionsFx.failData,
        $$api.advisor.getAdvisorPricingOptionsFx.failData,
        $$api.advisor.getGroupPricingOptionsFx.failData,
        $$api.advisor.setAdvisorPropertiesFx.failData,
    ],
    target: createEffect(AlertsService.handleError),
});

// saving advisors changes
sample({ clock: [$$api.advisor.removeClientFx.doneData], target: saveAdvisorsChangesFx });

// $$api.advisor.getClients SUCCESS handler
sample({
    clock: $$api.advisor.getClientsFx.done,
    target: createEffect(
        ({
            params,
            result: data,
        }: {
            params: { participantId: number } | { groupId: number } | undefined;
            result: Client[];
        }) => {
            if (!params) {
                const currentParticipant = $$auth.$currentParticipant.getState();
                storage.set(
                    (store): MyStore => ({
                        ...store,
                        advisor: {
                            ...(store.advisor || {}),
                            clients: {
                                group: store.advisor?.clients?.group || {},
                                participant: {
                                    ...(store.advisor?.clients?.participant || {}),
                                    [currentParticipant?.participant_id || -1]: data,
                                },
                            },
                        },
                    }),
                );
            } else {
                if ('participantId' in params) {
                    storage.set(
                        (store): MyStore => ({
                            ...store,
                            advisor: {
                                ...(store.advisor || {}),
                                clients: {
                                    group: store.advisor?.clients?.group || {},
                                    participant: {
                                        ...(store.advisor?.clients?.participant || {}),
                                        [params.participantId || -1]: data,
                                    },
                                },
                            },
                        }),
                    );
                }
                if ('groupId' in params) {
                    storage.set(
                        (store): MyStore => ({
                            ...store,
                            advisor: {
                                ...(store.advisor || {}),
                                clients: {
                                    participant: store.advisor?.clients?.participant || {},
                                    group: {
                                        ...(store.advisor?.clients?.group || {}),
                                        [params.groupId || -1]: data,
                                    },
                                },
                            },
                        }),
                    );
                }
            }
        },
    ),
});

// $$api.advisor.removeClient SUCCESS handler
sample({
    clock: $$api.advisor.removeClientFx.doneData,
    fn: () => 'FETCH_CLIENTS',
    target: createEffect(eventDispatch),
});
sample({
    clock: $$api.advisor.removeClientFx.doneData,
    fn: () => undefined,
    target: $$api.advisor.getClientsFx,
});

// $$api.advisor.getAdvisorInviteCodes SUCCESS handler
sample({
    source: $global,
    clock: $$api.advisor.getAdvisorInviteCodesFx.done,
    fn: (store, { params, result }): MyStore => ({
        ...store,
        advisor: {
            ...(store.advisor || {}),
            inviteCodes: {
                ...(store.advisor?.inviteCodes || {}),
                [params || -1]: result,
            },
        },
    }),
    target: $global,
});

// $$api.advisor.createZendeskTicket SUCCESS handler
sample({
    clock: $$api.advisor.createZendeskTicketFx.doneData,
    fn: () => t('advisor.sendFeedbackModal.successMessage'),
    target: createEffect(AlertsService.showSuccess),
});

// $$api.advisor.getAllGroups SUCCESS handler
sample({
    source: $global,
    clock: $$api.advisor.getAllGroupsFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        advisor: {
            ...(store.advisor || {}),
            groups: _.keyBy(data, 'groupId'),
        },
    }),
    target: $global,
});

// $$api.advisor.getGroup SUCCESS handler
sample({
    source: $global,
    clock: $$api.advisor.getGroupFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        advisor: {
            ...(store.advisor || {}),
            groups: {
                ...(store.advisor?.groups || {}),
                [data.groupId]: data,
            },
        },
    }),
    target: $global,
});

// $$api.advisor.createGroup SUCCESS handler
sample({
    clock: $$api.advisor.createGroupFx.doneData,
    target: [saveAdvisorsChangesFx, createEffect(() => AlertsService.showSuccess('Group successfully created'))],
});

// $$api.advisor.removeAdvisorFromGroup SUCCESS handler
sample({
    clock: $$api.advisor.removeAdvisorFromGroupFx.doneData,
    target: [saveAdvisorsChangesFx, createEffect(() => AlertsService.showSuccess('Advisor successfully removed'))],
});

// $$api.advisor.moveAdvisorToGroup SUCCESS handler
sample({
    clock: $$api.advisor.moveAdvisorToGroupFx.doneData,
    target: [saveAdvisorsChangesFx, createEffect(() => AlertsService.showSuccess('Advisor successfully moved'))],
});

// $$api.advisor.moveGroupToNewParentGroup SUCCESS handler
sample({
    clock: $$api.advisor.moveGroupToNewParentGroupFx.doneData,
    target: [saveAdvisorsChangesFx, createEffect(() => AlertsService.showSuccess('Group successfully moved'))],
});

// $$api.advisor.editGroup SUCCESS handler
sample({
    clock: $$api.advisor.editGroupFx.doneData,
    target: [saveAdvisorsChangesFx, createEffect(() => AlertsService.showSuccess('Group successfully updated'))],
});

// $$api.advisor.removeGroup SUCCESS handler
sample({
    clock: $$api.advisor.removeGroupFx.doneData,
    target: [saveAdvisorsChangesFx, createEffect(() => AlertsService.showSuccess('Group successfully removed'))],
});

// $$api.advisor.linkClientToAdvisor SUCCESS handler
sample({
    clock: $$api.advisor.linkClientToAdvisorFx.doneData,
    target: [saveAdvisorsChangesFx],
});

// $$api.advisor.changeAdvisor SUCCESS handler
sample({
    clock: $$api.advisor.changeAdvisorFx.doneData,
    target: [
        saveAdvisorsChangesFx,
        createEffect(() => eventDispatch('FETCH_CLIENTS')),
        createEffect(() => AlertsService.showSuccess('Advisor successfully changed')),
    ],
});

// $$api.advisor.linkAdvisorToGroup SUCCESS handler
sample({
    clock: $$api.advisor.linkAdvisorToGroupFx.doneData,
    target: [
        saveAdvisorsChangesFx,
        createEffect(() => AlertsService.showSuccess('Advisor successfully linked to group')),
    ],
});

// $$api.advisor.sendInvite SUCCESS handler
sample({
    clock: $$api.advisor.sendInviteFx.doneData,
    target: createEffect(() => AlertsService.showSuccess('Invite successfully sent')),
});

// $$api.advisor.getGroupByCode SUCCESS handler
sample({
    source: $global,
    clock: $$api.advisor.getGroupByCodeFx.doneData,
    fn: (store, data): MyStore => ({
        ...store,
        advisor: {
            ...(store.advisor || {}),
            groups: {
                ...(store.advisor?.groups || {}),
                [data.groupId]: data,
            },
        },
    }),
    target: $global,
});

// $$api.advisor.setAdvisorProperties SUCCESS handler
sample({
    source: $global,
    clock: $$api.advisor.setAdvisorPropertiesFx.doneData.map(({ participants }) =>
        keyBy(participants, 'participant_id'),
    ),
    fn: (global, participants): MyStore => ({
        ...global,
        participants: { ...(global?.participants || {}), ...participants },
    }),
    target: $global,
});
sample({
    clock: $$api.advisor.setAdvisorPropertiesFx.doneData,
    target: createEffect(() => AlertsService.showSuccess('Advisor successfully updated')),
});
